import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { Ticket, EmailLog, TicketSla, DevelopmentLog, DevelopmentLogEntry } from '../types';

interface TicketFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  /** Comma-separated list of statuses (e.g. "ENCORSA_IN_PROGRESS,ENCORSA_ESTIMATING") */
  statusIn?: string;
  priority?: string;
  projectId?: string;
  assignedToId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

function buildQuery(filters: TicketFilters) {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(filters)) {
    if (val !== undefined && val !== '') params.set(key, String(val));
  }
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function useTickets(filters: TicketFilters = {}) {
  return useQuery({
    queryKey: ['tickets', filters],
    queryFn: () => api.get<Ticket[]>(`/tickets${buildQuery(filters)}`),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ['tickets', id],
    queryFn: () => api.get<Ticket>(`/tickets/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000,
    retry: 1,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { subject: string; description: string; priority?: string; projectId: string; subcategoryId: string; createdById?: string; clientElementId?: number }) =>
      api.post<Ticket>('/tickets', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); },
  });
}

type TicketMutationOptions = { onSuccess?: () => void; onError?: (error: Error) => void };

export function useUpdateTicket(options?: TicketMutationOptions) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api.patch<Ticket>(`/tickets/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); options?.onSuccess?.(); },
    onError: (error) => { options?.onError?.(error); },
  });
}

export function useAssignTicket(options?: TicketMutationOptions) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assignedToId, supervisorId }: { id: string; assignedToId: string; supervisorId?: string }) =>
      api.post<Ticket>(`/tickets/${id}/assign`, { assignedToId, supervisorId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); options?.onSuccess?.(); },
    onError: (error) => { options?.onError?.(error); },
  });
}

export function useTransitionTicket(options?: TicketMutationOptions) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, commentBody, commentIsInternal }: { id: string; action: string; commentBody?: string; commentIsInternal?: boolean }) =>
      api.post<Ticket>(`/tickets/${id}/transition`, { action, commentBody, commentIsInternal }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['comments'] });
      options?.onSuccess?.();
    },
    onError: (error) => { options?.onError?.(error); },
  });
}

export function useCloseTicketWithCsat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, score, comment }: { id: string; score: number; comment?: string }) =>
      api.post<Ticket>(`/tickets/${id}/csat`, { score, comment }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); },
  });
}

export function useReopenTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Ticket>(`/tickets/${id}/reopen`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); },
  });
}

export function useDeleteTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tickets/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); },
  });
}

export function useTicketEmails(id: string) {
  return useQuery({
    queryKey: ['tickets', id, 'emails'],
    queryFn: () => api.get<EmailLog[]>(`/tickets/${id}/emails`),
    enabled: !!id,
  });
}

export function useTicketSla(id: string) {
  return useQuery({
    queryKey: ['tickets', id, 'sla'],
    queryFn: () => api.get<TicketSla>(`/tickets/${id}/sla`),
    enabled: !!id,
    refetchInterval: 60000, // refresh every minute for countdown
  });
}

export function useTicketSlaBatch(ticketIds: string[]) {
  return useQuery({
    queryKey: ['tickets', 'sla-batch', ticketIds],
    queryFn: () => api.post<Record<string, TicketSla>>('/tickets/sla-batch', { ticketIds }),
    enabled: ticketIds.length > 0,
    staleTime: 60 * 1000,
    refetchInterval: 60000,
  });
}

export function useDevelopmentLogs(ticketId: string) {
  return useQuery({
    queryKey: ['tickets', ticketId, 'development-logs'],
    queryFn: () => api.get<DevelopmentLog[]>(`/tickets/development-logs/${ticketId}`),
    enabled: !!ticketId,
    staleTime: 30 * 1000,
  });
}

export function useSaveDevelopmentLogs(options?: TicketMutationOptions) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, entries }: { ticketId: string; entries: DevelopmentLogEntry[] }) =>
      api.put<Ticket>(`/tickets/development-logs/${ticketId}`, { entries }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      options?.onSuccess?.();
    },
    onError: (error) => { options?.onError?.(error); },
  });
}
